/**
 * カテゴリカル変数をダミー変数化したデータセットを作成
 */

import type { DataSet, EphemeralDataSet, ColumnSchema, DataValue, ColumnId } from '../../types';
import { createColumnId, createColumnName, createDatasetId } from '../../types';
import { encodeCategoricalVariable } from './categorical-encoder';
import type { CategoricalEncoding } from './categorical-encoder';
import { DatasetAccessor } from './DatasetAccessor';
import logger from './logger';

/**
 * ダミー変数化されたデータセットの結果
 */
export interface DummyCodedDatasetResult {
  /** ダミー変数化されたデータセット（EphemeralDataSet） */
  dataset: EphemeralDataSet;
  /** カテゴリカル変数のエンコーディング情報 */
  categoricalEncodings: CategoricalEncoding[];
  /** 数値変数の列ID（元の順序を保持） */
  numericColumnIds: ColumnId[];
}

/**
 * データセットのカテゴリカル変数をダミー変数化する
 *
 * 指定された列のうち、string型の列をTreatment codingでダミー変数に変換し、
 * 新しいEphemeralDataSetを作成する。
 *
 * @param dataset - 元のデータセット
 * @param columnIds - 変換対象の列ID（数値型とstring型の両方を含む）
 * @param columnsToEncode - ダミー変数化する列IDのセット（オプション、未指定時は自動判定）
 * @param scaleOverrides - 出力データセットのスケールオーバーライド（オプション）
 * @param columnsToKeepOriginal - ダミー変数化する際に元の列も残す列IDのセット（オプション）
 * @returns ダミー変数化されたデータセットと関連情報
 */
export function createDummyCodedDataset(
  dataset: DataSet,
  columnIds: ColumnId[],
  columnsToEncode?: Set<ColumnId>,
  scaleOverrides?: Record<string, 'nominal' | 'ordinal' | 'interval' | 'ratio'>,
  columnsToKeepOriginal?: Set<ColumnId>
): DummyCodedDatasetResult {
  logger.assert(columnIds.length > 0, 'columnIds must not be empty');
  logger.assert(dataset.schema?.columns != null, 'dataset.schema.columns must not be null');

  // DatasetAccessorはデータを持つデータセットのみを受け付ける
  if (!('data' in dataset) || dataset.data === undefined) {
    throw new Error('Dataset must have data property');
  }

  const accessor = new DatasetAccessor(dataset as DataSet & { data: Record<ColumnId, DataValue[]> });
  const rowCount = accessor.getRowCount();

  // 列スキーマを取得
  const columnSchemas = columnIds.map(colId => {
    const col = dataset.schema!.columns.find(c => c.id === colId);
    if (!col) {
      throw new Error(`Column schema not found for ID ${colId}`);
    }
    return col;
  });

  // カテゴリカル変数と数値変数を分離
  const numericColumns: Array<{ schema: ColumnSchema; data: DataValue[] }> = [];
  const categoricalEncodings: CategoricalEncoding[] = [];
  const dummyColumnSchemas: ColumnSchema[] = [];
  const dummyColumnData: DataValue[][] = [];

  for (let i = 0; i < columnIds.length; i++) {
    const colId = columnIds[i];
    const colSchema = columnSchemas[i];
    const colData = accessor.getColumnDataById(colId);

    // スケールをオーバーライドする場合は、オーバーライド後のスケールを使用
    const effectiveScale = scaleOverrides?.[colId] ?? colSchema.scale?.scale;

    // ダミー変数化するかどうかを判定
    let shouldEncode = false;
    if (columnsToEncode) {
      // 明示的に指定された場合
      shouldEncode = columnsToEncode.has(colId);
    } else {
      // 自動判定：尺度に基づいてカテゴリカル変数かどうかを判定
      // ただし、boolean型は除外（既に2値なので）
      shouldEncode = (effectiveScale === 'nominal' ||
                     effectiveScale === 'ordinal') &&
                     colSchema.type !== 'boolean';
    }

    if (shouldEncode) {
      // カテゴリカル変数（名義尺度・順序尺度）をエンコード
      const encoded = encodeCategoricalVariable(colData, colId, colSchema.name);
      categoricalEncodings.push(encoded.encoding);

      // 元の列も残すかどうかを確認
      const keepOriginal = columnsToKeepOriginal?.has(colId) ?? false;
      if (keepOriginal) {
        // 元の列を数値列として追加
        let outputSchema = colSchema;
        if (scaleOverrides?.[colId] && colSchema.scale) {
          outputSchema = {
            ...colSchema,
            scale: {
              ...colSchema.scale,
              scale: scaleOverrides[colId]
            }
          };
        }
        numericColumns.push({ schema: outputSchema, data: colData });
      }

      // ダミー変数の列スキーマとデータを作成
      encoded.encoding.dummyVariableNames.forEach((dummyName, dummyIdx) => {
        const dummyColumnId = createColumnId(`${colId}_dummy_${dummyIdx}`);
        dummyColumnSchemas.push({
          id: dummyColumnId,
          name: createColumnName(dummyName),
          type: 'int64',  // ダミー変数は0/1の整数
          scale: {
            scale: 'nominal',
            inferredAutomatically: false
          },
          nullable: false
        });
        dummyColumnData.push(encoded.dummyColumns[dummyIdx]);
      });

      logger.info('[createDummyCodedDataset] Encoded categorical variable', {
        columnName: colSchema.name,
        columnType: colSchema.type,
        scale: colSchema.scale?.scale,
        nCategories: encoded.encoding.categories.length,
        nDummyVariables: encoded.dummyColumns.length,
        referenceCategory: encoded.encoding.referenceCategory,
        keepOriginal
      });
    } else {
      // 数値変数（間隔尺度・比率尺度）やboolean型はそのまま
      // スケールオーバーライドがあれば適用
      let outputSchema = colSchema;
      if (scaleOverrides?.[colId] && colSchema.scale) {
        outputSchema = {
          ...colSchema,
          scale: {
            ...colSchema.scale,
            scale: scaleOverrides[colId]
          }
        };
      }
      numericColumns.push({ schema: outputSchema, data: colData });
    }
  }

  // 新しいスキーマを作成（数値変数 + ダミー変数）
  const newColumns: ColumnSchema[] = [
    ...numericColumns.map(nc => nc.schema),
    ...dummyColumnSchemas
  ];

  // 新しいデータを作成
  const newData: Record<string, DataValue[]> = {};

  // 数値変数のデータをコピー
  numericColumns.forEach(nc => {
    newData[nc.schema.id] = nc.data;
  });

  // ダミー変数のデータを追加
  dummyColumnSchemas.forEach((schema, idx) => {
    newData[schema.id] = dummyColumnData[idx];
  });

  // EphemeralDataSetを作成
  const ephemeralDataset: EphemeralDataSet = {
    id: createDatasetId(crypto.randomUUID()),
    name: `${dataset.name} (Dummy Coded)`,
    type: 'ephemeral',
    schema: {
      columns: newColumns
    },
    rowCount,
    data: newData,
    metadata: {
      sourceDatasetId: dataset.id,
      purpose: 'temporary_analysis',
      description: `Dummy-coded dataset with ${categoricalEncodings.length} categorical variable(s) encoded`,
      createdAt: new Date().toISOString()
    }
  };

  logger.info('[createDummyCodedDataset] Created dummy-coded dataset', {
    originalColumnCount: columnIds.length,
    numericColumnCount: numericColumns.length,
    categoricalColumnCount: categoricalEncodings.length,
    totalDummyVariables: dummyColumnSchemas.length,
    newColumnCount: newColumns.length,
    rowCount
  });

  return {
    dataset: ephemeralDataset,
    categoricalEncodings,
    numericColumnIds: numericColumns.map(nc => nc.schema.id)
  };
}
